import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import styled from 'styled-components'
import {
  Select, Spin, Tag, Table, Input, Typography, message,
  Modal, InputNumber, Button, Switch, Checkbox, Drawer, Tooltip, Slider,
} from 'antd'
import {
  SearchOutlined, CopyOutlined, CheckCircleOutlined, ClockCircleOutlined,
  EnvironmentOutlined, LeftOutlined, RightOutlined, InfoCircleOutlined,
  FilterOutlined, ToolOutlined, ApartmentOutlined, BankOutlined,
  ExperimentOutlined, MedicineBoxOutlined, BookOutlined, AppstoreOutlined,
  TeamOutlined, UserOutlined, TrophyOutlined, CloseCircleOutlined,
  ThunderboltOutlined, MailOutlined, SettingOutlined, WarningOutlined,
} from '@ant-design/icons'
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer,
} from 'recharts'
import adminRequest from 'utils/adminRequest'
import ErrorMessage from 'utils/ErrorMessage'

const { Text } = Typography

// ── Styled ─────────────────────────────────────────────────────

const SummaryGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 14px;
  background: linear-gradient(135deg, #e8edff 0%, #f5f0ff 40%, #e8f8f2 100%);
  padding: 10px 0 8px;
  margin-top: -8px;
`

const KPICard = styled.div`
  background: ${p => p.$active ? 'rgba(255,255,255,0.90)' : 'rgba(255,255,255,0.55)'};
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255,255,255,0.75);
  border-left: ${p => p.$active ? `4px solid ${p.$color || '#3249D7'}` : '1px solid rgba(255,255,255,0.75)'};
  border-radius: 16px;
  padding: 13px 16px;
  display: flex;
  align-items: center;
  gap: 11px;
  flex: 0 0 auto;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: ${p => p.$active
    ? `0 6px 28px ${p.$color || '#3249D7'}22`
    : '0 4px 24px rgba(50,73,215,0.07)'};
  transition: transform 0.15s, box-shadow 0.15s;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(50,73,215,0.12); }
`

const KPIIcon = styled.div`
  width: 38px; height: 38px;
  border-radius: 10px;
  background: ${p => p.$bg || '#eef0fb'};
  display: flex; align-items: center; justify-content: center;
  font-size: 16px;
  color: ${p => p.$color || '#3249D7'};
  flex-shrink: 0;
`

const KPIVal = styled.div`font-size: 20px; font-weight: 800; color: #1a1a2e; line-height: 1;`
const KPILbl = styled.div`font-size: 11px; color: #666; margin-top: 2px;`
const KPISub = styled.div`font-size: 10px; color: #999; margin-top: 3px;`

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 14px;
  margin-top: 24px;
  margin-bottom: 8px;
`

const ChartBox = styled.div`
  background: rgba(255,255,255,0.65);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255,255,255,0.75);
  border-radius: 14px;
  padding: 14px 16px;
  box-shadow: 0 4px 20px rgba(50,73,215,0.06);
`

const ChartTitle = styled.div`
  font-size: 11px; font-weight: 700; color: #777;
  text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 10px;
`

const RoleInfoCard = styled.div`
  background: rgba(255,255,255,0.65);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255,255,255,0.75);
  border-radius: 16px;
  padding: 14px 20px;
  margin-bottom: 16px;
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  align-items: center;
  box-shadow: 0 4px 24px rgba(50,73,215,0.07);
`

const SummaryBar = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  margin-bottom: 14px;
  padding: 10px 16px;
  background: rgba(255,255,255,0.55);
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.7);
  flex-wrap: wrap;
`

const SummaryItem = styled.div`
  display: flex; align-items: center; gap: 6px;
  font-size: 13px; color: #444;
  span.num { font-weight: 700; font-size: 16px; color: ${p => p.$color || '#1a1a2e'}; }
`

const ScoreFilterWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  background: #fff;
  border: 2px solid ${p => p.$active ? '#3249D7' : '#d9d9d9'};
  border-radius: 10px;
  overflow: hidden;
  transition: border-color 0.2s;
  box-shadow: ${p => p.$active ? '0 0 0 3px rgba(50,73,215,0.12)' : 'none'};
`

const ScoreLabel = styled.div`
  padding: 0 12px;
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.$active ? '#3249D7' : '#555'};
  background: ${p => p.$active ? '#eef0fb' : '#f5f5f5'};
  height: 40px;
  display: flex;
  align-items: center;
  white-space: nowrap;
  border-right: 2px solid ${p => p.$active ? '#3249D7' : '#d9d9d9'};
  transition: all 0.2s;
`

// ── Constants ──────────────────────────────────────────────────

const ROLE_CATEGORIES = [
  { key: 'engineering', label: 'Technical / Eng',    color: '#0891b2', bg: '#e0f7fa', icon: <ToolOutlined /> },
  { key: 'management',  label: 'Management / Ops',  color: '#7c3aed', bg: '#f5f0ff', icon: <ApartmentOutlined /> },
  { key: 'finance',     label: 'Banking / Finance', color: '#059669', bg: '#e8f8f2', icon: <BankOutlined /> },
  { key: 'science',     label: 'Science',            color: '#d97706', bg: '#fef3c7', icon: <ExperimentOutlined /> },
  { key: 'medical',     label: 'Medical / Pharma',  color: '#dc2626', bg: '#fee2e2', icon: <MedicineBoxOutlined /> },
  { key: 'anydegree',   label: 'Any Degree',         color: '#475569', bg: '#f1f5f9', icon: <BookOutlined /> },
  { key: 'other',       label: 'Other',              color: '#94a3b8', bg: '#f8fafc', icon: <AppstoreOutlined /> },
]

// Candidate filter drawer — drives the server-side filter params on
// /meta-dashboard/job-roles/:roleId/potential. Employment Type / Work
// Style / Company were removed (they describe roles, not candidates)
// and a Status filter was added in their place.
const FILTER_CATS = [
  { key: 'status', label: 'Status' },
  { key: 'degree', label: 'Degree' },
  { key: 'skill',  label: 'Skills' },
]

const STATUS_OPTIONS = [
  { value: 'APPLIED',     label: 'Applied' },
  { value: 'NOT_APPLIED', label: 'Not Applied' },
]

const EMPTY_PENDING = { status: [], degree: [], skill: [] }

const WS_COLORS  = { REMOTE: '#059669', ONSITE: '#3249D7', HYBRID: '#d97706' }

const wsLabel = v => ({ REMOTE: 'Remote', ONSITE: 'On-site', HYBRID: 'Hybrid' })[v] || (v || 'Not Set')
const etLabel = v => (v || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

// ── Role classification ─────────────────────────────────────────

const classifyRole = (role) => {
  if (role.is_for_all) return 'anydegree'
  const all = [...(role.degree_names || []), ...(role.degree_short_forms || [])]
  if (all.length === 0) return 'other'

  const has = (...kws) => kws.some(kw =>
    all.some(n => kw.startsWith('^') ? n === kw.slice(1) : n.includes(kw))
  )

  if (has('engineer', 'b.e', '^be', 'b.tech', '^btech', 'm.tech', '^mtech',
          'diploma', 'computer applic', 'computer science', 'data science',
          'information tech', 'b.c.a', '^bca', 'm.c.a', '^mca', 'polytechnic',
          'bachelor of eng', 'master of tech', 'bachelor of tech'))
    return 'engineering'

  if (has('mba', 'bba', 'pgdm', 'pgdba', 'management', 'business admin',
          'personnel management', 'master of business', '^mpm', 'operations management'))
    return 'management'

  if (has('commerce', 'b.com', 'm.com', 'chartered account', 'finance', 'economics',
          'banking', '^ca', '^cfa', '^icwa'))
    return 'finance'

  if (has('science', 'b.sc', 'm.sc', '^bsc', '^msc'))
    return 'science'

  if (has('medicine', 'surgery', 'pharma', 'nursing', 'dental', 'rehabilit',
          'veterinary', 'mbbs', 'unani', 'ayurveda', 'medical', 'health science'))
    return 'medical'

  return 'other'
}

// ── Helpers ────────────────────────────────────────────────────

const formatVacancy = (min, max) => {
  if (min == null && max == null) return null
  const hi = Math.max(min || 0, max || 0)
  if (hi === 0) return { label: '0 Vacancies', isZero: true }
  const parts = [min, max].filter(v => v != null && v > 0)
  return { label: `${parts.join('–')} vacancies`, isZero: false }
}

const computeWeightedScore = (s, w) => {
  const degreeNorm = (s.degree_pts || 0) / 40
  const skillNorm  = (s.skill_pts  || 0) / 20
  const marksNorm  = ((parseFloat(s.marks_pts) || 0) + (parseFloat(s.tenth_pts) || 0)) / 25
  const arrearNorm = (s.arrear_pts || 0) / 10
  const locNorm    = (s.location_pts || 0) / 5
  const score = degreeNorm * (w.degree || 0) + skillNorm * (w.skill || 0) +
                marksNorm * (w.marks || 0)   + arrearNorm * (w.arrear || 0) + locNorm * (w.location || 0)
  return Math.min(100, Math.round(score))
}

const formatCTC = (min, max, period) => {
  if (!min && !max) return null
  const fmt = v => v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : `₹${v.toLocaleString()}`
  const range = min && max ? `${fmt(min)} – ${fmt(max)}` : fmt(min || max)
  const per = period === 'ANNUAL' ? '/yr' : period === 'MONTHLY' ? '/mo' : period ? `/${period.toLowerCase()}` : ''
  return `${range}${per}`
}

const pct = v => (v != null && !isNaN(v)) ? `${parseFloat(v).toFixed(1)}%` : '—'

const getRoleSkills = (role) => {
  if (!role) return []
  const s = role.required_skills
  if (Array.isArray(s)) return s.filter(Boolean)
  if (typeof s === 'string') { try { return JSON.parse(s).filter(Boolean) } catch { return [] } }
  return []
}

const renderEligibility = (role) => {
  if (!role) return null
  const items = []

  if (parseFloat(role.avg_scroe) > 0)    items.push({ label: 'Diploma / Avg Marks', value: `≥ ${parseFloat(role.avg_scroe)}%` })
  if (parseFloat(role.tenth_score) > 0)  items.push({ label: '10th Marks',          value: `≥ ${parseFloat(role.tenth_score)}%` })
  if (parseFloat(role.twelth_score) > 0) items.push({ label: '12th Marks',          value: `≥ ${parseFloat(role.twelth_score)}%` })
  if (parseFloat(role.ug_score) > 0)     items.push({ label: 'UG Marks',            value: `≥ ${parseFloat(role.ug_score)}%` })
  if (parseFloat(role.pg_score) > 0)     items.push({ label: 'PG Marks',            value: `≥ ${parseFloat(role.pg_score)}%` })

  if (role.is_arrears_allowed === false)
    items.push({ label: 'Active Arrears', value: 'None allowed' })
  else if (role.is_arrears_allowed === true)
    items.push({ label: 'Active Arrears', value: role.arrear_count > 0 ? `Up to ${role.arrear_count}` : 'Allowed (no limit set)' })

  if (role.is_history_arrears === true)
    items.push({ label: 'History Arrears', value: role.history_arrear_count > 0 ? `Up to ${role.history_arrear_count}` : 'Considered (no limit)' })
  else if (role.is_history_arrears === false)
    items.push({ label: 'History Arrears', value: 'None allowed' })

  const years = Array.isArray(role.year_of_passing) ? role.year_of_passing : []
  if (years.length > 0) items.push({ label: 'Batch Year(s)', value: years.join(', ') })

  if (role.is_for_all) {
    items.push({ label: 'Degree / Stream', value: 'All degrees accepted' })
  } else if (Array.isArray(role.eligibility_criteria) && role.eligibility_criteria.length > 0) {
    const flat = role.eligibility_criteria.flat().filter(e => e && (e.degreeStreamMapId || e.degreeId))
    if (flat.length > 0)
      items.push({ label: 'Degree / Stream', value: `${flat.length} specific combination${flat.length !== 1 ? 's' : ''} required` })
  }

  if (items.length === 0)
    return <div style={{ color: '#999', fontSize: 13, padding: '12px 0' }}>No specific eligibility criteria configured for this role.</div>

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <tbody>
        {items.map((item, i) => (
          <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
            <td style={{ padding: '10px 8px', color: '#555', fontSize: 13, width: '45%', fontWeight: 500 }}>{item.label}</td>
            <td style={{ padding: '10px 8px', color: '#1a1a2e', fontSize: 13, fontWeight: 600 }}>{item.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ── Candidate match breakdown ──────────────────────────────────

const buildMatchBreakdown = (student, role) => {
  if (!student || !role) return []
  const rows = []

  // Degree / Stream (top priority in scoring)
  const requiredDsmIds = role.is_for_all ? [] :
    (role.eligibility_criteria || []).flat()
      .map(e => e?.degreeStreamMapId).filter(Boolean).map(String)
  const studentDsmId = student.degree_stream_map_id ? String(student.degree_stream_map_id) : null
  let degreeStatus, degreeNote
  if (role.is_for_all) {
    degreeStatus = 'pass'; degreeNote = 'All degrees accepted'
  } else if (requiredDsmIds.length === 0) {
    degreeStatus = 'pass'; degreeNote = 'No restriction set'
  } else if (!studentDsmId) {
    degreeStatus = 'info'; degreeNote = 'Degree not confirmed (benefit of doubt)'
  } else if (requiredDsmIds.includes(studentDsmId)) {
    degreeStatus = 'pass'; degreeNote = 'Exact degree match'
  } else {
    degreeStatus = 'neutral'; degreeNote = 'Degree mapping uncertain'
  }
  rows.push({
    criterion: 'Degree / Stream',
    candidate: [student.degree, student.department].filter(Boolean).join(' · ') || '—',
    requirement: degreeNote,
    status: degreeStatus,
  })

  // Skill match
  const roleSkills = getRoleSkills(role)
  if (roleSkills.length > 0) {
    rows.push({
      criterion: 'Skills Required',
      candidate: `${student.skill_match_count || 0} of ${student.required_skill_count || roleSkills.length} matched`,
      requirement: roleSkills.slice(0, 5).join(', ') + (roleSkills.length > 5 ? ` +${roleSkills.length - 5} more` : ''),
      status: (student.skill_match_count || 0) > 0 ? 'pass' : 'neutral',
    })
  }

  // Location
  rows.push({
    criterion: 'Location',
    candidate: student.location_match ? 'Matches preferred location' : 'Outside preferred location',
    requirement: 'Location preference',
    status: student.location_match ? 'pass' : 'neutral',
  })

  // Diploma / Avg marks
  if (parseFloat(role.avg_scroe) > 0) {
    const cand = parseFloat(student.diploma_marks) || 0
    const req  = parseFloat(role.avg_scroe)
    rows.push({
      criterion: 'Diploma / Avg Marks',
      candidate: `${cand.toFixed(1)}%`,
      requirement: `≥ ${req}%`,
      status: cand >= req ? 'pass' : 'fail',
    })
  } else if (student.diploma_marks != null) {
    rows.push({
      criterion: 'Diploma / Avg Marks',
      candidate: `${parseFloat(student.diploma_marks).toFixed(1)}%`,
      requirement: 'No minimum set',
      status: 'neutral',
    })
  }

  // Arrears
  const activeArrears = student.no_of_arrears || 0
  if (role.is_arrears_allowed === false) {
    rows.push({
      criterion: 'Active Arrears',
      candidate: `${activeArrears}`,
      requirement: 'None allowed',
      status: activeArrears === 0 ? 'pass' : 'fail',
    })
  } else if (role.is_arrears_allowed === true) {
    const limit = role.arrear_count || 0
    rows.push({
      criterion: 'Active Arrears',
      candidate: `${activeArrears}`,
      requirement: limit > 0 ? `Up to ${limit}` : 'Allowed (no limit)',
      status: (limit === 0 || activeArrears <= limit) ? 'pass' : 'fail',
    })
  } else {
    rows.push({
      criterion: 'Active Arrears',
      candidate: `${activeArrears}`,
      requirement: 'No restriction',
      status: 'neutral',
    })
  }

  // Batch year
  const allowedYears = Array.isArray(role.year_of_passing) ? role.year_of_passing : []
  rows.push({
    criterion: 'Batch Year',
    candidate: student.batch_year ? `${student.batch_year}` : '—',
    requirement: allowedYears.length > 0 ? allowedYears.join(', ') : 'No restriction',
    status: allowedYears.length === 0 ? 'neutral' : allowedYears.includes(student.batch_year) ? 'pass' : 'fail',
  })

  // Application status
  rows.push({
    criterion: 'Application Status',
    candidate: student.already_applied ? 'Already Applied' : 'Not yet applied',
    requirement: '—',
    status: student.already_applied ? 'applied' : 'neutral',
  })

  return rows
}

const statusIcon = (status) => {
  if (status === 'pass')    return <CheckCircleOutlined style={{ color: '#059669', fontSize: 15 }} />
  if (status === 'fail')    return <CloseCircleOutlined style={{ color: '#dc2626', fontSize: 15 }} />
  if (status === 'applied') return <CheckCircleOutlined style={{ color: '#3249D7', fontSize: 15 }} />
  if (status === 'info')    return <InfoCircleOutlined  style={{ color: '#d97706', fontSize: 15 }} />
  return <span style={{ display: 'inline-block', width: 15, height: 15, borderRadius: '50%', background: '#e5e7eb' }} />
}

const statusBg = (status) => {
  if (status === 'pass')    return '#f0fdf4'
  if (status === 'fail')    return '#fff5f5'
  if (status === 'applied') return '#eef0fb'
  return 'transparent'
}

// ── Table columns ──────────────────────────────────────────────
//
// Sortable columns set `sorter: true` (controlled mode) — the actual
// ordering happens server-side via sortBy/sortOrder, dispatched from
// Table's onChange. Per-column `filters`/`onFilter` were removed: the
// Filter Drawer is the single source of truth for filtering now so the
// API can return accurate totals.

const buildColumns = (allEmails, onCandidateClick, sortState) => [
  {
    title: 'Name', dataIndex: 'full_name', key: 'name', width: 170,
    sorter: true,
    sortOrder: sortState?.field === 'full_name' ? sortState.order : null,
    onCell: () => ({ className: 'col-name' }),
    render: (v, record) => v
      ? (
        <span
          style={{ color: '#3249D7', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 }}
          onClick={() => onCandidateClick(record)}
        >
          {v}
        </span>
      )
      : '—',
  },
  {
    title: 'College / Dept', key: 'college', width: 210,
    render: (_, r) => (
      <div>
        <Text ellipsis={{ tooltip: r.institute_campus_name }} style={{ maxWidth: 200, display: 'block', fontSize: 13 }}>{r.institute_campus_name || '—'}</Text>
        {(r.degree || r.department) && (
          <Text ellipsis={{ tooltip: [r.degree, r.department].filter(Boolean).join(' · ') }} style={{ maxWidth: 200, display: 'block', fontSize: 11, color: '#888' }}>
            {[r.degree, r.department].filter(Boolean).join(' · ')}
          </Text>
        )}
      </div>
    ),
  },
  {
    title: 'Batch', dataIndex: 'batch_year', key: 'batch', width: 62, align: 'center',
    sorter: true,
    sortOrder: sortState?.field === 'batch_year' ? sortState.order : null,
    render: v => v || '—',
  },
  {
    title: 'Marks%', dataIndex: 'diploma_marks', key: 'marks', width: 78, align: 'center',
    sorter: true,
    sortOrder: sortState?.field === 'diploma_marks' ? sortState.order : null,
    render: v => pct(v),
  },
  {
    title: 'Arrears', dataIndex: 'no_of_arrears', key: 'arrears', width: 72, align: 'center',
    sorter: true,
    sortOrder: sortState?.field === 'no_of_arrears' ? sortState.order : null,
    render: v => { const n = v || 0; return <Tag color={n === 0 ? 'green' : n <= 2 ? 'orange' : 'red'} style={{ fontSize: 11 }}>{n}</Tag> },
  },
  {
    title: <span><EnvironmentOutlined style={{ marginRight: 3 }} />Loc</span>,
    dataIndex: 'location_match', key: 'loc', width: 72, align: 'center',
    render: v => v
      ? <Tag color="green" style={{ fontSize: 10 }}>✓</Tag>
      : <span style={{ color: '#ccc', fontSize: 12 }}>—</span>,
  },
  {
    title: 'Score', dataIndex: 'match_score', key: 'score', width: 68, align: 'center',
    sorter: true,
    sortOrder: sortState?.field === 'match_score' ? sortState.order : null,
    render: v => {
      const n = parseFloat(v) || 0
      const color = n >= 70 ? '#059669' : n >= 50 ? '#d97706' : '#6b7280'
      return <strong style={{ color, fontSize: 14 }}>{n}</strong>
    },
  },
  {
    title: () => (
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        Email
        {allEmails.length > 0 && (
          <CopyOutlined
            style={{ cursor: 'pointer', color: '#3249D7', fontSize: 12 }}
            title="Copy all emails"
            onClick={e => {
              e.stopPropagation()
              navigator.clipboard.writeText(allEmails.join(', '))
                .then(() => message.success(`Copied ${allEmails.length} emails`))
                .catch(() => message.error('Copy failed'))
            }}
          />
        )}
      </span>
    ),
    dataIndex: 'primary_email', key: 'email', width: 200,
    render: v => v
      ? <Text copyable={{ tooltips: false }} ellipsis={{ tooltip: v }} style={{ maxWidth: 185, fontSize: 12 }}>{v}</Text>
      : <span style={{ color: '#ccc' }}>—</span>,
  },
  {
    title: 'Status', dataIndex: 'already_applied', key: 'status', width: 100, align: 'center',
    sorter: true,
    sortOrder: sortState?.field === 'already_applied' ? sortState.order : null,
    render: v => v
      ? <Tag icon={<CheckCircleOutlined />} color="green" style={{ fontSize: 11 }}>Applied</Tag>
      : <Tag icon={<ClockCircleOutlined />} color="orange" style={{ fontSize: 11 }}>Not Applied</Tag>,
  },
]

// ── Component ──────────────────────────────────────────────────

const DEFAULT_PAGE_SIZE = 10

const FindCandidatesTab = () => {
  const [roles, setRoles]                     = useState([])
  const [rolesLoading, setRolesLoading]       = useState(true)
  const [selectedRoleId, setSelectedRoleId]   = useState(null)
  const [potential, setPotential]             = useState(null)
  const [potLoading, setPotLoading]           = useState(false)
  const [search, setSearch]                   = useState('')
  const [minMatch, setMinMatch]               = useState(0)
  const [locationOnly, setLocationOnly]       = useState(false)
  // Server-driven pagination: page/pageSize are sent to the API and
  // total comes back in the response (potential.total).
  const [page, setPage]                       = useState(1)
  const [pageSize, setPageSize]               = useState(DEFAULT_PAGE_SIZE)
  // Sort state mirrors Ant Table's sorter: { field, order: 'ascend'|'descend' }.
  // Default mirrors the previous defaultSortOrder on the Score column.
  const [sortState, setSortState]             = useState({ field: 'match_score', order: 'descend' })
  // Filter Drawer options { degrees: [], skills: [] } seeded once per
  // role from the API's filter_options payload. selectedRole.degree_names
  // is empty for is_for_all roles, so we can't depend on it for the
  // Degree dropdown; this comes from the actual eligible candidates.
  const [filterOptions, setFilterOptions]     = useState({ degrees: [], skills: [] })
  const [roleDrawerOpen, setRoleDrawerOpen]   = useState(false)
  const [candidateDrawer, setCandidateDrawer] = useState({ open: false, student: null })
  const [cardFilter, setCardFilter]           = useState(null)
  const [roleFilterOpen, setRoleFilterOpen]   = useState(false)
  const [pendingFilters, setPendingFilters]   = useState(EMPTY_PENDING)
  const [activeFilters, setActiveFilters]     = useState(EMPTY_PENDING)
  const [activeCat, setActiveCat]             = useState('status')
  const [catSearch, setCatSearch]             = useState('')

  // Bulk email
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectAllLoading, setSelectAllLoading] = useState(false)
  const [emailModal, setEmailModal]           = useState({ open: false })
  const [emailSubject, setEmailSubject]       = useState('')
  const [emailMessage, setEmailMessage]       = useState('')
  const [emailSending, setEmailSending]       = useState(false)

  // Score weights (degree=40, skill=20, marks=25, arrear=10, location=5 → total 100)
  const DEFAULT_WEIGHTS = { degree: 40, skill: 20, marks: 25, arrear: 10, location: 5 }
  const [weights, setWeights]               = useState(DEFAULT_WEIGHTS)
  const [pendingWeights, setPendingWeights] = useState(DEFAULT_WEIGHTS)
  const [weightsOpen, setWeightsOpen]       = useState(false)

  const fetchedRef  = useRef(false)
  const tableWrapRef = useRef(null)

  // ── Load roles ────────────────────────────────────────────────

  useEffect(() => {
    if (fetchedRef.current) return
    fetchedRef.current = true
    adminRequest.get('/meta-dashboard/active-job-roles')
      .then(res => {
        const list = res?.data?.data || []
        setRoles(list)
        if (list.length > 0) setSelectedRoleId(prev => prev ?? String(list[0].id))
      })
      .catch(err => ErrorMessage(err?.response?.data?.message || 'Failed to load job roles'))
      .finally(() => setRolesLoading(false))
  }, [])

  // ── Server-side fetch ─────────────────────────────────────────
  //
  // Every change to role / search / minMatch / filters / sort /
  // page / pageSize fires a single POST here — the backend applies
  // everything and returns the requested page slice plus the total.
  // The whole filter+sort payload travels in the body so multi-select
  // arrays (skill, degree, status) don't bloat the URL.

  const fetchPotential = useCallback((roleId, opts) => {
    const {
      search: searchVal = '',
      minScore: minScoreVal = 0,
      filters: filtersVal = EMPTY_PENDING,
      sort: sortVal = { field: 'match_score', order: 'descend' },
      page: pageVal = 1,
      pageSize: pageSizeVal = DEFAULT_PAGE_SIZE,
      withOptions = false,
    } = opts || {}

    const sortByMap = {
      full_name: 'full_name', match_score: 'match_score',
      diploma_marks: 'diploma_marks', no_of_arrears: 'no_of_arrears',
      batch_year: 'batch_year', already_applied: 'already_applied',
    }

    const body = {
      page:  pageVal,
      limit: pageSizeVal,
      sortBy:    sortByMap[sortVal?.field] || 'match_score',
      sortOrder: sortVal?.order === 'ascend' ? 'ASC' : 'DESC',
    }
    if (searchVal && searchVal.trim())   body.search   = searchVal.trim()
    if (minScoreVal > 0)                 body.minScore = minScoreVal
    if (filtersVal.status?.length === 1) body.status   = filtersVal.status[0]
    if (filtersVal.skill?.length)        body.skill    = filtersVal.skill
    if (filtersVal.degree?.length)       body.degree   = filtersVal.degree
    // First call per role asks for filter_options so the drawer's
    // degree/skill dropdowns get populated; subsequent calls skip it.
    if (withOptions) body.withOptions = true

    setPotLoading(true)
    adminRequest.post(`/meta-dashboard/job-roles/${roleId}/potential`, body)
      .then(res => {
        const payload = res?.data?.data || null
        setPotential(payload)
        if (payload?.filter_options) {
          setFilterOptions({
            degrees: payload.filter_options.degrees || [],
            skills:  payload.filter_options.skills  || [],
          })
        }
      })
      .catch(err => ErrorMessage(err?.response?.data?.message || 'Failed to load candidates'))
      .finally(() => setPotLoading(false))
  }, [])

  // Fetch the IDs of every matching candidate (all pages) so the
  // user can "Select all across pages" for bulk email. Re-uses the
  // current filter / sort / search state on the FE; backend re-runs
  // the same filter SQL with idsOnly=true and returns just id+email.
  const fetchAllMatchingIds = useCallback(async () => {
    if (!selectedRoleId) return []
    const body = {
      idsOnly: true,
      search:    (search || '').trim() || undefined,
      minScore:  minMatch > 0 ? minMatch : undefined,
      status:    activeFilters.status?.length === 1 ? activeFilters.status[0] : undefined,
      skill:     activeFilters.skill?.length  ? activeFilters.skill  : undefined,
      degree:    activeFilters.degree?.length ? activeFilters.degree : undefined,
      sortBy:    sortState?.field || undefined,
      sortOrder: sortState?.order === 'ascend' ? 'ASC' : sortState?.order === 'descend' ? 'DESC' : undefined,
    }
    try {
      setSelectAllLoading(true)
      const res = await adminRequest.post(`/meta-dashboard/job-roles/${selectedRoleId}/potential`, body)
      const ids = res?.data?.data?.ids || []
      return ids.filter(r => r.primary_email).map(r => r.id)
    } catch (e) {
      ErrorMessage(e?.response?.data?.message || 'Could not fetch all matching candidates')
      return []
    } finally {
      setSelectAllLoading(false)
    }
  }, [selectedRoleId, search, minMatch, activeFilters, sortState])

  // Wraps fetchPotential with the current state so callers (effects /
  // table-onChange / drawer apply) don't have to thread the same six
  // arguments through every call site.
  const refetch = useCallback((overrides = {}) => {
    if (!selectedRoleId) return
    fetchPotential(selectedRoleId, {
      search, minScore: minMatch, filters: activeFilters, sort: sortState,
      page, pageSize, ...overrides,
    })
  }, [selectedRoleId, search, minMatch, activeFilters, sortState, page, pageSize, fetchPotential])

  // Selecting a different role resets all candidate-level filters,
  // jumps back to page 1, and re-fires the fetch with the fresh state.
  // Sets withOptions=true so the Filter Drawer's degree/skill lists get
  // seeded from the actual eligible candidates for the new role.
  useEffect(() => {
    if (!selectedRoleId) return
    const freshSort = null
    setPotential(null)
    setSearch('')
    setMinMatch(0)
    setLocationOnly(false)
    setActiveFilters(EMPTY_PENDING)
    setPendingFilters(EMPTY_PENDING)
    setSortState(freshSort)
    setPage(1)
    setPageSize(DEFAULT_PAGE_SIZE)
    setFilterOptions({ degrees: [], skills: [] })
    fetchPotential(selectedRoleId, {
      search: '', minScore: 0, filters: EMPTY_PENDING, sort: freshSort,
      page: 1, pageSize: DEFAULT_PAGE_SIZE,
      withOptions: true,
    })
  }, [selectedRoleId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce the search field at 500ms so we don't fire on every
  // keystroke; any search change snaps pagination back to page 1 so
  // the user lands on the first matches for the new query rather than
  // on an empty page-N. minMatch follows the same pattern with a
  // shorter delay since it's a discrete spinner.
  const searchDebounceRef = useRef(null)
  useEffect(() => {
    if (!selectedRoleId) return
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => {
      setPage(1)
      fetchPotential(selectedRoleId, {
        search, minScore: minMatch, filters: activeFilters, sort: sortState,
        page: 1, pageSize,
      })
    }, 500)
    return () => clearTimeout(searchDebounceRef.current)
  }, [search]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedRoleId) return
    const handle = setTimeout(() => {
      setPage(1)
      fetchPotential(selectedRoleId, {
        search, minScore: minMatch, filters: activeFilters, sort: sortState,
        page: 1, pageSize,
      })
    }, 400)
    return () => clearTimeout(handle)
  }, [minMatch]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── KPI card data ─────────────────────────────────────────────

  const kpi = useMemo(() => {
    const agg = list => ({
      count:   list.length,
      applied: list.reduce((s, r) => s + (r.applied_count || 0), 0),
      placed:  list.reduce((s, r) => s + (r.placed_count  || 0), 0),
    })
    const byKey = {}
    ROLE_CATEGORIES.forEach(cat => {
      byKey[cat.key] = agg(roles.filter(r => classifyRole(r) === cat.key))
    })
    return { all: agg(roles), ...byKey }
  }, [roles])

  // ── Filtered roles ────────────────────────────────────────────
  // The card-level role-category chips still scope which roles show up
  // in the dropdown. The Filter Drawer's degree/skill/status now apply
  // to CANDIDATES (server-side), not to the roles list — so they no
  // longer participate here.
  const filteredRoles = useMemo(() => {
    if (!cardFilter) return roles
    return roles.filter(r => classifyRole(r) === cardFilter)
  }, [roles, cardFilter])

  // ── Auto-select first role when filter changes ────────────────

  useEffect(() => {
    if (filteredRoles.length === 0) return
    const inList = filteredRoles.some(r => String(r.id) === selectedRoleId)
    if (!inList) setSelectedRoleId(String(filteredRoles[0].id))
  }, [filteredRoles]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Dropdown options ──────────────────────────────────────────

  const roleOptions = useMemo(
    () => filteredRoles.map(r => ({ value: String(r.id), label: `${r.company_name} — ${r.title}` })),
    [filteredRoles],
  )

  const handleRoleChange = useCallback(v => setSelectedRoleId(v), [])

  const selectedRole = useMemo(
    () => roles.find(r => String(r.id) === selectedRoleId),
    [roles, selectedRoleId],
  )

  // ── Chart data ────────────────────────────────────────────────

  const topCompaniesChart = useMemo(() => {
    const map = {}
    filteredRoles.forEach(r => {
      if (!map[r.company_name]) map[r.company_name] = { name: r.company_name, applied: 0, placed: 0 }
      map[r.company_name].applied += (r.applied_count || 0)
      map[r.company_name].placed  += (r.placed_count  || 0)
    })
    return Object.values(map).sort((a, b) => b.applied - a.applied).slice(0, 8)
  }, [filteredRoles])

  const workStyleChart = useMemo(() => {
    const map = {}
    filteredRoles.forEach(r => { const ws = r.work_style || 'Not Set'; map[ws] = (map[ws] || 0) + 1 })
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value)
  }, [filteredRoles])

  // ── Candidate filter drawer helpers ───────────────────────────
  //
  // Status options are static; degree and skill options come from the
  // API's filter_options payload (requested once per role on the first
  // fetch). selectedRole.degree_names is empty for is_for_all roles, so
  // we deliberately don't fall back to it — filterOptions reflects the
  // actual eligible candidates and stays correct for every role type.

  const getCatOptions = useCallback((cat) => {
    const q = catSearch.toLowerCase()
    let opts = []
    if (cat === 'status') {
      opts = STATUS_OPTIONS
    } else if (cat === 'degree') {
      opts = (filterOptions.degrees || []).map(v => ({ value: v, label: v.replace(/\b\w/g, c => c.toUpperCase()) }))
    } else if (cat === 'skill') {
      opts = (filterOptions.skills || []).map(v => ({ value: v, label: v }))
    }
    return q ? opts.filter(o => o.label.toLowerCase().includes(q)) : opts
  }, [filterOptions, catSearch])

  const catCount = key => (pendingFilters[key] || []).length

  const toggleCheck = (cat, val) => setPendingFilters(pf => {
    const cur = pf[cat] || []
    // Status is single-select (Applied vs Not Applied are mutually
    // exclusive); the rest stay multi-select.
    if (cat === 'status') {
      return { ...pf, status: cur.includes(val) ? [] : [val] }
    }
    return { ...pf, [cat]: cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val] }
  })

  const handleOpenFilter = () => {
    setPendingFilters({ ...activeFilters })
    setActiveCat('status'); setCatSearch(''); setRoleFilterOpen(true)
  }

  // Applying drawer filters always resets to page 1 and refetches so
  // the new total + page slice match the new filter set.
  const handleApplyFilter = () => {
    setActiveFilters({ ...pendingFilters })
    setRoleFilterOpen(false)
    setPage(1)
    if (selectedRoleId) {
      fetchPotential(selectedRoleId, {
        search, minScore: minMatch, filters: pendingFilters, sort: sortState,
        page: 1, pageSize,
      })
    }
  }

  // Removing a chip should also refetch with the trimmed filter set.
  const clearFilter = useCallback((key, val) => {
    setActiveFilters(prev => {
      const next = { ...prev, [key]: (prev[key] || []).filter(v => v !== val) }
      setPage(1)
      if (selectedRoleId) {
        fetchPotential(selectedRoleId, {
          search, minScore: minMatch, filters: next, sort: sortState,
          page: 1, pageSize,
        })
      }
      return next
    })
  }, [selectedRoleId, search, minMatch, sortState, pageSize, fetchPotential])

  const activeChipCount = Object.values(activeFilters).reduce((s, arr) => s + arr.length, 0)

  // ── Candidates filtering ──────────────────────────────────────

  // Defensive de-duplication by student id. The server now returns one row
  // per candidate, but guard the table against any duplicate id slipping
  // through (e.g. a backend join fan-out regression) so a student can never
  // be rendered twice. First occurrence wins, order preserved.
  const students = useMemo(() => {
    const rows = potential?.students || []
    const seen = new Set()
    return rows.filter(r => {
      const key = r?.id ?? r?.student_id
      if (key == null) return true
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [potential])

  // Re-compute weighted scores client-side when the user has customised
  // the score weights. With default weights the server's match_score is
  // authoritative, and we MUST NOT re-sort here — that would clobber the
  // server's ORDER BY (e.g. sortBy=diploma_marks would still get
  // displayed in match_score order). The client-side reorder only fires
  // when both (a) the user has custom weights AND (b) they're sorting by
  // Match Score; otherwise we hand the rows back in server order.
  const studentsWithScores = useMemo(() => {
    if (!students.length) return students
    const isDefault = (
      weights.degree === DEFAULT_WEIGHTS.degree && weights.skill === DEFAULT_WEIGHTS.skill &&
      weights.marks  === DEFAULT_WEIGHTS.marks  && weights.arrear === DEFAULT_WEIGHTS.arrear &&
      weights.location === DEFAULT_WEIGHTS.location
    )
    if (isDefault) return students
    const recomputed = students.map(s => ({ ...s, match_score: computeWeightedScore(s, weights) }))
    if (sortState.field !== 'match_score') return recomputed
    const dir = sortState.order === 'ascend' ? 1 : -1
    return recomputed.sort((a, b) => ((a.match_score || 0) - (b.match_score || 0)) * dir)
  }, [students, weights, sortState])  // eslint-disable-line react-hooks/exhaustive-deps

  // Any-degree-type entries from eligibility_criteria (e.g. isAllDegree:true with degreeType "DIPLOMA")
  const anyDegreeTypes = useMemo(() => {
    if (!selectedRole || selectedRole.is_for_all) return []
    const criteria = Array.isArray(selectedRole.eligibility_criteria) ? selectedRole.eligibility_criteria : []
    const seen = new Set()
    criteria.flat().forEach(e => {
      if (e?.isAllDegree === true && e.degreeType) seen.add(String(e.degreeType).toUpperCase())
    })
    return [...seen]
  }, [selectedRole])

  // The location-only toggle still trims the *current page* client-side —
  // it isn't surfaced as a backend filter yet, so the visible page may
  // shrink while the pager continues to reflect the unfiltered total.
  const filtered = useMemo(() => {
    let result = studentsWithScores
    if (locationOnly) result = result.filter(s => s.location_match)
    return result
  }, [studentsWithScores, locationOnly])

  const allEmails = useMemo(() => filtered.map(s => s.primary_email).filter(Boolean), [filtered])

  const ctc = selectedRole
    ? formatCTC(selectedRole.min_compensation, selectedRole.max_compensation, selectedRole.ctc_period)
    : null

  // Server-driven totals: backend returns the post-filter row count as
  // `total` (with `total_eligible` kept as a backward-compat alias).
  // appliedCount/notAppliedCount are per-page numbers — full-set totals
  // aren't available without a separate aggregation endpoint.
  const totalEligible   = potential?.total ?? potential?.total_eligible ?? 0
  const appliedCount    = students.filter(s => s.already_applied).length
  const notAppliedCount = students.length - appliedCount

  // Ant Table fires onChange for ANY pager / sorter / column-filter
  // change in one go (pagination, filters, sorter). We honour pagination
  // and sorter; column filters were stripped from the column defs so
  // `filters` is always {}. Resetting to page 1 on sort change keeps the
  // first row aligned with the new sort. Changing pageSize also resets.
  const handleTableChange = useCallback((pag, _filters, sorter) => {
    const nextPageSize = pag?.pageSize || pageSize
    // sorter may arrive as an array (multi-column) — Find Candidates is
    // single-column so we always read the first entry / object form.
    const s = Array.isArray(sorter) ? sorter[0] : sorter
    const nextSort = s?.order
  ? {
      field: s.field || s.columnKey,
      order: s.order,
    }
  : null

const currentField = sortState?.field
const currentOrder = sortState?.order

const sortChanged =
  nextSort?.field !== currentField ||
  nextSort?.order !== currentOrder
    const pageSizeChanged = nextPageSize !== pageSize
    const nextPage = (sortChanged || pageSizeChanged) ? 1 : (pag?.current || 1)

    if (
  nextPage === page &&
  nextPageSize === pageSize &&
  nextSort?.field === currentField &&
  nextSort?.order === currentOrder
) {
  return
}

    setPage(nextPage)
    setPageSize(nextPageSize)
    setSortState(nextSort)
    if (selectedRoleId) {
      fetchPotential(selectedRoleId, {
        search, minScore: minMatch, filters: activeFilters, sort: nextSort,
        page: nextPage, pageSize: nextPageSize,
      })
    }
  }, [selectedRoleId, search, minMatch, activeFilters, sortState, page, pageSize, fetchPotential])

  const scrollTable = (delta) => {
    const el = tableWrapRef.current?.querySelector('.ant-table-content')
            || tableWrapRef.current?.querySelector('.ant-table-body')
    if (el) el.scrollLeft += delta
  }

  const handleCandidateClick = useCallback((student) => {
    setCandidateDrawer({ open: true, student })
  }, [])

  const matchBreakdown = useMemo(
    () => buildMatchBreakdown(candidateDrawer.student, selectedRole),
    [candidateDrawer.student, selectedRole],
  )

  const candidateScore = useMemo(() => {
    if (!candidateDrawer.student) return 0
    return computeWeightedScore(candidateDrawer.student, weights)
  }, [candidateDrawer.student, weights])

  const weightsTotal = useMemo(
    () => Object.values(pendingWeights).reduce((s, v) => s + (Number(v) || 0), 0),
    [pendingWeights],
  )

  const handleOpenEmailModal = useCallback(() => {
    setEmailSubject(`Invitation to Apply: ${selectedRole?.title || 'Job Opportunity'} at ${selectedRole?.company_name || ''}`)
    setEmailMessage(`We would like to invite you to apply for the ${selectedRole?.title || ''} position at ${selectedRole?.company_name || ''}. This opportunity aligns well with your profile and we encourage you to apply at the earliest.`)
    setEmailModal({ open: true })
  }, [selectedRole])

  const handleSendEmails = useCallback(async () => {
    setEmailSending(true)
    try {
      const res = await adminRequest.post(`/meta-dashboard/job-roles/${selectedRoleId}/send-bulk-email`, {
        studentIds: selectedRowKeys,
        subject: emailSubject,
        message: emailMessage,
      })
      const { sent = 0, skipped = 0, failed = 0 } = res?.data?.data || {}
      message.success(`Sent to ${sent} student${sent !== 1 ? 's' : ''}${skipped > 0 ? ` · ${skipped} had no email` : ''}${failed > 0 ? ` · ${failed} failed` : ''}`)
      setEmailModal({ open: false })
      setSelectedRowKeys([])
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to send emails')
    } finally {
      setEmailSending(false)
    }
  }, [selectedRoleId, selectedRowKeys, emailSubject, emailMessage])

  const handleWeightsApply = useCallback(() => {
    setWeights({ ...pendingWeights })
    setWeightsOpen(false)
  }, [pendingWeights])

  const selectedRoleSkills = useMemo(() => getRoleSkills(selectedRole), [selectedRole])

  // ── Render ─────────────────────────────────────────────────────

  const vacancyInfo = selectedRole ? formatVacancy(selectedRole.min_vacancies, selectedRole.max_vacancies) : null
  const isWeightCustom = Object.entries(weights).some(([k, v]) => v !== DEFAULT_WEIGHTS[k])

  return (
    <div>
      <style>{`
        .row-no-email td { opacity: 0.42; }
        .row-no-email td.col-name { opacity: 1; }
        .row-no-email td.col-name span { color: #3249D7 !important; cursor: pointer !important; opacity: 1; }
      `}</style>

      {/* ── KPI Cards (sticky) ── */}
      <SummaryGrid>
        <KPICard $active={cardFilter === null} $color="#3249D7" onClick={() => setCardFilter(null)}>
          <KPIIcon $color="#3249D7" $bg="#eef0fb"><TeamOutlined /></KPIIcon>
          <div>
            <KPIVal>{kpi.all.count}</KPIVal>
            <KPILbl>All Active Roles</KPILbl>
            <KPISub>{kpi.all.applied.toLocaleString()} applied · {kpi.all.placed.toLocaleString()} placed</KPISub>
          </div>
        </KPICard>

        {ROLE_CATEGORIES.filter(cat => (kpi[cat.key]?.count || 0) > 0).map(cat => (
          <KPICard
            key={cat.key}
            $active={cardFilter === cat.key}
            $color={cat.color}
            onClick={() => setCardFilter(prev => prev === cat.key ? null : cat.key)}
          >
            <KPIIcon $color={cat.color} $bg={cat.bg}>{cat.icon}</KPIIcon>
            <div>
              <KPIVal>{kpi[cat.key].count}</KPIVal>
              <KPILbl>{cat.label}</KPILbl>
              <KPISub>{kpi[cat.key].applied.toLocaleString()} applied · {kpi[cat.key].placed.toLocaleString()} placed</KPISub>
            </div>
          </KPICard>
        ))}
      </SummaryGrid>

      {/* ── Role info card ── */}
      {selectedRole && (
        <RoleInfoCard>
          <div style={{ marginRight: 4 }}>
            <div
              style={{ fontSize: 15, fontWeight: 700, color: '#3249D7', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 }}
              onClick={() => setRoleDrawerOpen(true)}
              title="Click to view full role details"
            >
              {selectedRole.title}
            </div>
            <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{selectedRole.company_name}</div>
          </div>
          {selectedRole.employment_type && <Tag color="blue">{etLabel(selectedRole.employment_type)}</Tag>}
          {selectedRole.work_style      && <Tag color="cyan">{wsLabel(selectedRole.work_style)}</Tag>}
          {ctc && <Tag color="green">{ctc}</Tag>}
          {vacancyInfo && (
            <span style={{ fontSize: 12, color: vacancyInfo.isZero ? '#dc2626' : '#666', fontWeight: vacancyInfo.isZero ? 600 : 400 }}>
              {vacancyInfo.label}
            </span>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 16, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#888' }}>
              Applied: <strong style={{ color: '#3249D7' }}>{(selectedRole.applied_count || 0).toLocaleString()}</strong>
            </span>
            <span style={{ fontSize: 12, color: '#888' }}>
              Placed: <strong style={{ color: '#7c3aed' }}>{(selectedRole.placed_count || 0).toLocaleString()}</strong>
            </span>
          </div>
        </RoleInfoCard>
      )}

      {/* ── First-load spinner ── */}
      {/* Subsequent refetches (page change, debounced search) flow through
          the Table's own `loading` prop, so the table stays visible. */}
      {potLoading && !potential && (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 12, color: '#888', fontSize: 13 }}>Matching candidates…</div>
        </div>
      )}

      {/* ── Candidates section ── */}
      {potential && (
        <>
          {/* Summary bar */}
          <SummaryBar>
            <SummaryItem $color="#3249D7">
              <span className="num">{totalEligible.toLocaleString()}</span> eligible
              <Tooltip
                title={
                  <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                    <strong>Match Score (0–100)</strong><br />
                    Degree match: up to {weights.degree}pts<br />
                    Skills: up to {weights.skill}pts<br />
                    Academic marks: up to {weights.marks}pts<br />
                    Arrears: up to {weights.arrear}pts<br />
                    Location: up to {weights.location}pts<br />
                    <span style={{ color: '#aaa' }}>Sorted by match score; paginated server-side.</span>
                  </div>
                }
              >
                <InfoCircleOutlined style={{ color: '#aaa', cursor: 'help', marginLeft: 4 }} />
              </Tooltip>
            </SummaryItem>
            <div style={{ width: 1, height: 20, background: '#ddd' }} />
            <SummaryItem $color="#059669">
              <CheckCircleOutlined style={{ color: '#059669' }} />
              <span className="num">{appliedCount.toLocaleString()}</span> applied
              <span style={{ fontSize: 11, color: '#aaa', marginLeft: 4 }}>(on page)</span>
            </SummaryItem>
            <SummaryItem $color="#e65100">
              <ClockCircleOutlined style={{ color: '#e65100' }} />
              <span className="num">{notAppliedCount.toLocaleString()}</span> not applied
              <span style={{ fontSize: 11, color: '#aaa', marginLeft: 4 }}>(on page)</span>
            </SummaryItem>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <Input
                placeholder="Search name, email, college, dept…"
                prefix={<SearchOutlined style={{ color: '#aaa' }} />}
                value={search} onChange={e => setSearch(e.target.value)}
                style={{ width: 240 }} allowClear
              />
              <ScoreFilterWrap $active={minMatch > 0}>
                <ScoreLabel $active={minMatch > 0}>Score ≥</ScoreLabel>
                <InputNumber
                  min={0} max={100} step={5}
                  value={minMatch}
                  onChange={v => setMinMatch(v || 0)}
                  style={{ width: 80, border: 'none', boxShadow: 'none', fontSize: 15, fontWeight: 700, height: 40 }}
                  controls
                  variant="borderless"
                />
              </ScoreFilterWrap>
              <Button
                icon={<SettingOutlined />}
                size="small"
                onClick={() => { setPendingWeights({ ...weights }); setWeightsOpen(true) }}
                style={isWeightCustom ? { borderColor: '#d97706', color: '#d97706', fontWeight: 600 } : {}}
                title="Configure score weights"
              >
                Weights{isWeightCustom ? ' ●' : ''}
              </Button>
              {/* ── Role selector + Filter button ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, fontSize: 14, color: '#333', whiteSpace: 'nowrap' }}>Active Job Role:</span>
        <Select
          showSearch loading={rolesLoading}
          value={selectedRoleId} onChange={handleRoleChange}
          placeholder="Select a job role…" style={{ width: 480 }}
          optionFilterProp="label" options={roleOptions}
          getPopupContainer={trigger => trigger.parentNode}
          notFoundContent={rolesLoading ? <Spin size="small" /> : 'No roles match filters'}
        />
        <span style={{ fontSize: 12, color: '#888' }}>
          {filteredRoles.length !== roles.length
            ? `${filteredRoles.length} of ${roles.length} roles`
            : `${roles.length} active role${roles.length !== 1 ? 's' : ''}`}
        </span>
        <Button
          icon={<FilterOutlined />}
          onClick={handleOpenFilter}
          style={activeChipCount ? { borderColor: '#3249D7', color: '#3249D7', fontWeight: 600 } : {}}
        >
          Filter{activeChipCount > 0 ? ` (${activeChipCount})` : ''}
        </Button>
      </div>

      {/* Active filter chips */}
      {(cardFilter || activeChipCount > 0) && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
          {cardFilter && (
            <Tag closable onClose={() => setCardFilter(null)} color="blue" style={{ borderRadius: 10 }}>
              {ROLE_CATEGORIES.find(c => c.key === cardFilter)?.label}
            </Tag>
          )}
          {activeFilters.status.map(v => (
            <Tag key={v} closable onClose={() => clearFilter('status', v)} color={v === 'APPLIED' ? 'green' : 'orange'} style={{ borderRadius: 10 }}>
              {STATUS_OPTIONS.find(o => o.value === v)?.label || v}
            </Tag>
          ))}
          {activeFilters.degree.map(v => (
            <Tag key={v} closable onClose={() => clearFilter('degree', v)} color="purple" style={{ borderRadius: 10 }}>
              {v.replace(/\b\w/g, c => c.toUpperCase())}
            </Tag>
          ))}
          {activeFilters.skill.map(v => (
            <Tag key={v} closable onClose={() => clearFilter('skill', v)} color="geekblue" style={{ borderRadius: 10 }}>
              {v}
            </Tag>
          ))}
        </div>
      )}
              {/* <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#555', whiteSpace: 'nowrap' }}>
                <EnvironmentOutlined />
                Location only
                <Switch size="small" checked={locationOnly} onChange={setLocationOnly} />
              </span> */}
            </div>
          </SummaryBar>

          {totalEligible === 0 && !potLoading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#bbb', fontSize: 14 }}>
              No eligible candidates found for this role based on eligibility criteria.
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: '#888' }}>
                  <strong>{totalEligible.toLocaleString()}</strong> potential candidate{totalEligible === 1 ? '' : 's'}
                </span>
                {selectedRowKeys.length > 0 && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<MailOutlined />}
                    onClick={handleOpenEmailModal}
                    style={{ background: '#059669', borderColor: '#059669' }}
                  >
                    Send Email ({selectedRowKeys.length})
                  </Button>
                )}
                {selectedRowKeys.length > 0 && (
                  <Button size="small" onClick={() => setSelectedRowKeys([])}>Clear selection</Button>
                )}
              </div>
              <div ref={tableWrapRef} style={{ position: 'relative' }}>
                {/* <LeftOutlined
                  onClick={() => scrollTable(-300)}
                  style={{ position: 'absolute', left: -28, top: '50%', transform: 'translateY(-50%)', zIndex: 10, fontSize: 46, color: 'rgba(50,73,215,0.28)', cursor: 'pointer', transition: 'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'rgba(50,73,215,0.72)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(50,73,215,0.28)'}
                />
                <RightOutlined
                  onClick={() => scrollTable(300)}
                  style={{ position: 'absolute', right: -28, top: '50%', transform: 'translateY(-50%)', zIndex: 10, fontSize: 46, color: 'rgba(50,73,215,0.28)', cursor: 'pointer', transition: 'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'rgba(50,73,215,0.72)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(50,73,215,0.28)'}
                /> */}
                <Table
                  rowKey="id"
                  dataSource={filtered}
                  columns={buildColumns(allEmails, handleCandidateClick, sortState)}
                  size="small"
                  scroll={{ x: 1050 }}
                  loading={potLoading}
                  onChange={handleTableChange}
                  rowSelection={{
                    selectedRowKeys,
                    onChange: keys => setSelectedRowKeys(keys),
                    getCheckboxProps: r => ({ disabled: !r.primary_email, title: !r.primary_email ? 'No email — cannot include in bulk send' : '' }),
                    // Header dropdown beside the check-all gets two extra
                    // actions: pick every candidate across all pages (for
                    // bulk email), and clear. Antd's default check-all
                    // only spans the current page.
                    selections: [
                      {
                        key: 'select-all-matching',
                        text: selectAllLoading
                          ? 'Loading…'
                          : `Select all ${totalEligible.toLocaleString()} matching (all pages)`,
                        onSelect: async () => {
                          const ids = await fetchAllMatchingIds()
                          if (ids.length) setSelectedRowKeys(ids)
                        },
                      },
                      {
                        key: 'clear-all',
                        text: 'Clear selection',
                        onSelect: () => setSelectedRowKeys([]),
                      },
                    ],
                  }}
                  pagination={{
                    current: page,
                    pageSize,
                    total: totalEligible,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '25', '50', '100'],
                    showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} potential candidates`,
                    position: ['bottomRight'],
                  }}
                  rowClassName={r => [
                    r.already_applied ? 'row-applied' : '',
                    !r.primary_email   ? 'row-no-email' : '',
                  ].filter(Boolean).join(' ')}
                />
              </div>
            </>
          )}

          {/* ── Charts (below table) ── */}
          {filteredRoles.length > 0 && (
            <ChartsGrid>
              <ChartBox>
                <ChartTitle>Applications by Company (Top 8)</ChartTitle>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={topCompaniesChart} layout="vertical" margin={{ left: 8, right: 16, top: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }}
                      interval={0}
                      tickFormatter={v => v && v.length > 20 ? v.slice(0, 20) + '…' : v} />
                    <RTooltip contentStyle={{ fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="applied" name="Applied" fill="#3249D7" radius={[0, 3, 3, 0]} />
                    <Bar dataKey="placed"  name="Placed"  fill="#059669" radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartBox>

              <ChartBox>
                <ChartTitle>Work Style Distribution</ChartTitle>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={workStyleChart} dataKey="value" nameKey="name"
                      cx="50%" cy="45%" innerRadius={48} outerRadius={76}>
                      {workStyleChart.map((entry, i) => (
                        <Cell key={i} fill={WS_COLORS[entry.name] || '#94a3b8'} />
                      ))}
                    </Pie>
                    <RTooltip formatter={(v, n) => [v, wsLabel(n)]} contentStyle={{ fontSize: 12 }} />
                    <Legend formatter={n => wsLabel(n)} wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartBox>
            </ChartsGrid>
          )}
        </>
      )}

      {!potLoading && !potential && selectedRoleId && !rolesLoading && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#bbb', fontSize: 14 }}>
          Could not load candidates for this role. Please try again.
        </div>
      )}

      {!rolesLoading && !selectedRoleId && (
        <div style={{ textAlign: 'center', padding: '80px 0', color: '#bbb', fontSize: 14 }}>
          Select a job role to see matching candidates
        </div>
      )}

      {/* ── Role Detail Drawer ── */}
      <Drawer
        title={
          <span>
            <InfoCircleOutlined style={{ marginRight: 8, color: '#3249D7' }} />
            Job Role Details
          </span>
        }
        open={roleDrawerOpen}
        onClose={() => setRoleDrawerOpen(false)}
        width={500}
        styles={{ body: { padding: 0 }, header: { padding: '16px 24px' } }}
      >
        {selectedRole && (
          <div style={{ padding: '20px 24px 20px 16px' }}>
            {/* Header */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#1a1a2e', lineHeight: 1.2 }}>{selectedRole.title}</div>
              <div style={{ fontSize: 14, color: '#555', marginTop: 4 }}>{selectedRole.company_name}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                {selectedRole.employment_type && <Tag color="blue">{etLabel(selectedRole.employment_type)}</Tag>}
                {selectedRole.work_style      && <Tag color="cyan">{wsLabel(selectedRole.work_style)}</Tag>}
                {ctc                          && <Tag color="green">{ctc}</Tag>}
              </div>
            </div>

            {/* Stats */}
            {(() => {
              const vi = formatVacancy(selectedRole.min_vacancies, selectedRole.max_vacancies)
              const stats = [
                { label: 'Applied',   value: (selectedRole.applied_count || 0).toLocaleString(), color: '#3249D7', bg: '#eef0fb' },
                { label: 'Placed',    value: (selectedRole.placed_count  || 0).toLocaleString(), color: '#7c3aed', bg: '#f5f0ff' },
                { label: 'Vacancies', value: vi ? vi.label : '—', color: vi?.isZero ? '#dc2626' : '#059669', bg: '#e8f8f2', small: vi?.isZero },
              ]
              return (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 24 }}>
                  {stats.map(s => (
                    <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
                      <div style={{ fontSize: s.small ? 12 : 20, fontWeight: 800, color: s.color, lineHeight: 1.3 }}>{s.value}</div>
                      <div style={{ fontSize: 11, color: '#777', marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              )
            })()}

            {/* Role category */}
            {(() => {
              const cat = ROLE_CATEGORIES.find(c => c.key === classifyRole(selectedRole))
              return cat ? (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>Role Category</div>
                  <Tag color={cat.color} style={{ fontSize: 13, padding: '2px 10px', borderRadius: 8 }}>{cat.label}</Tag>
                </div>
              ) : null
            })()}

            {/* Required Degrees */}
            {(() => {
              const degNames = selectedRole.degree_names || []
              const hasContent = selectedRole.is_for_all || degNames.length > 0 || anyDegreeTypes.length > 0
              if (!hasContent) return null
              return (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Required Degrees & Departments</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {selectedRole.is_for_all && (
                      <Tag color="default" style={{ borderRadius: 6, fontSize: 12 }}>All Degrees Accepted</Tag>
                    )}
                    {/* "Any DIPLOMA / Any UG / Any PG" entries from eligibility_criteria */}
                    {anyDegreeTypes.map((dt, i) => (
                      <Tag key={`any-${i}`} color="blue" style={{ borderRadius: 6, fontSize: 12 }}>Any {dt}</Tag>
                    ))}
                    {/* Specific degree names */}
                    {degNames.map((d, i) => (
                      <Tag key={`deg-${i}`} color="purple" style={{ borderRadius: 6, fontSize: 12 }}>
                        {d.replace(/\b\w/g, c => c.toUpperCase())}
                      </Tag>
                    ))}
                  </div>
                  {!selectedRole.is_for_all && Array.isArray(selectedRole.eligibility_criteria) && (() => {
                    const flat = selectedRole.eligibility_criteria.flat().filter(e => e?.degreeStreamMapId)
                    return flat.length > 0
                      ? <div style={{ fontSize: 11, color: '#999', marginTop: 6 }}>{flat.length} specific degree-stream combination{flat.length !== 1 ? 's' : ''} required</div>
                      : null
                  })()}
                </div>
              )
            })()}

            {/* Required Skills */}
            {selectedRoleSkills.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>
                  <ThunderboltOutlined style={{ marginRight: 4, color: '#d97706' }} />
                  Required Skills ({selectedRoleSkills.length})
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {selectedRoleSkills.map((sk, i) => (
                    <Tag key={i} color="orange" style={{ borderRadius: 6, fontSize: 12 }}>{sk}</Tag>
                  ))}
                </div>
              </div>
            )}

            {/* Eligibility criteria */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Eligibility Criteria</div>
              {renderEligibility(selectedRole)}
            </div>
          </div>
        )}
      </Drawer>

      {/* ── Candidate Match Drawer ── */}
      <Drawer
        title={
          <span>
            <UserOutlined style={{ marginRight: 8, color: '#3249D7' }} />
            {candidateDrawer.student?.full_name || 'Candidate'} — JD Match
          </span>
        }
        open={candidateDrawer.open}
        onClose={() => setCandidateDrawer({ open: false, student: null })}
        width={480}
        styles={{ body: { padding: '20px 24px' }, header: { padding: '16px 24px' } }}
      >
        {candidateDrawer.student && (
          <>
            {/* Score hero */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              {(() => {
                const score = candidateScore
                const color = score >= 70 ? '#059669' : score >= 50 ? '#d97706' : '#dc2626'
                const label = score >= 70 ? 'Strong Match' : score >= 50 ? 'Partial Match' : 'Low Match'
                return (
                  <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: `${color}14`, border: `2px solid ${color}30`, borderRadius: 16, padding: '18px 36px' }}>
                    <TrophyOutlined style={{ fontSize: 28, color }} />
                    <div style={{ fontSize: 48, fontWeight: 900, color, lineHeight: 1 }}>{score}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color, opacity: 0.85 }}>{label}</div>
                    <div style={{ fontSize: 11, color: '#999' }}>Overall Match Score</div>
                    {isWeightCustom && (
                      <div style={{ fontSize: 10, color: '#d97706', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, padding: '2px 8px', marginTop: 2 }}>
                        Custom weights applied
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>

            {/* Candidate basics */}
            <div style={{ marginBottom: 20, padding: '12px 14px', background: '#f8f9ff', borderRadius: 10, fontSize: 13 }}>
              <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 6 }}>{candidateDrawer.student.full_name}</div>
              {candidateDrawer.student.institute_campus_name && <div style={{ color: '#555' }}>{candidateDrawer.student.institute_campus_name}</div>}
              {(candidateDrawer.student.degree || candidateDrawer.student.department) && (
                <div style={{ color: '#777', marginTop: 2 }}>{[candidateDrawer.student.degree, candidateDrawer.student.department].filter(Boolean).join(' · ')}</div>
              )}
              {candidateDrawer.student.primary_email && <div style={{ color: '#3249D7', marginTop: 4 }}>{candidateDrawer.student.primary_email}</div>}
            </div>

            {/* Match breakdown */}
            <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10, paddingLeft: 8 }}>
              Criteria Match vs. {selectedRole?.title}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                  <th style={{ padding: '6px 8px', fontSize: 11, color: '#aaa', fontWeight: 600, textAlign: 'left', width: '35%' }}>Criterion</th>
                  <th style={{ padding: '6px 8px', fontSize: 11, color: '#aaa', fontWeight: 600, textAlign: 'left' }}>Candidate</th>
                  <th style={{ padding: '6px 8px', fontSize: 11, color: '#aaa', fontWeight: 600, textAlign: 'left' }}>Requirement</th>
                  <th style={{ padding: '6px 8px', fontSize: 11, color: '#aaa', fontWeight: 600, textAlign: 'center', width: 32 }}></th>
                </tr>
              </thead>
              <tbody>
                {matchBreakdown.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', background: statusBg(row.status) }}>
                    <td style={{ padding: '10px 8px', fontSize: 12, color: '#555', fontWeight: 500 }}>{row.criterion}</td>
                    <td style={{ padding: '10px 8px', fontSize: 13, color: '#1a1a2e', fontWeight: 600 }}>{row.candidate}</td>
                    <td style={{ padding: '10px 8px', fontSize: 12, color: '#777' }}>{row.requirement}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'center' }}>{statusIcon(row.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Drawer>

      {/* ── Role filter modal ── */}
      <Modal
        open={roleFilterOpen}
        onCancel={() => setRoleFilterOpen(false)}
        footer={null}
        width={620}
        title={<span style={{ fontSize: 14, fontWeight: 600 }}>Filter Candidates</span>}
        styles={{ body: { padding: 0 } }}
      >
        <div style={{ display: 'flex', height: 400 }}>
          {/* Left sidebar */}
          <div style={{ width: 160, borderRight: '1px solid #f0f0f0', overflowY: 'auto', flexShrink: 0 }}>
            {FILTER_CATS.map(cat => {
              const cnt = catCount(cat.key)
              return (
                <div
                  key={cat.key}
                  onClick={() => { setActiveCat(cat.key); setCatSearch('') }}
                  style={{
                    padding: '11px 14px', cursor: 'pointer',
                    background: activeCat === cat.key ? '#f0f2ff' : 'white',
                    borderLeft: `3px solid ${activeCat === cat.key ? '#3249D7' : 'transparent'}`,
                    color: activeCat === cat.key ? '#3249D7' : '#444',
                    fontWeight: activeCat === cat.key ? 600 : 400,
                    fontSize: 13,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    borderBottom: '1px solid #f7f7f7', userSelect: 'none',
                  }}
                >
                  {cat.label}
                  {cnt > 0 && (
                    <span style={{ background: '#3249D7', color: '#fff', borderRadius: 10, fontSize: 10, padding: '1px 6px', fontWeight: 700 }}>
                      {cnt}
                    </span>
                  )}
                </div>
              )
            })}
          </div>

          {/* Right panel */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0' }}>
              <Input
                size="small"
                prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                placeholder="Search…"
                value={catSearch}
                onChange={e => setCatSearch(e.target.value)}
                allowClear
              />
            </div>
            {/* Select-all / Clear toolbar — selects every option currently
                visible after the search filter. Mirrors corporate-react's
                MultipleCheckBoxWithDeSelect pattern. */}
            {(() => {
              const visible       = getCatOptions(activeCat)
              const selectedSet   = new Set(pendingFilters[activeCat] || [])
              const visibleValues = visible.map(o => o.value)
              const visibleSelectedCount = visibleValues.filter(v => selectedSet.has(v)).length
              const allVisibleSelected   = visible.length > 0 && visibleSelectedCount === visible.length
              const someVisibleSelected  = visibleSelectedCount > 0 && !allVisibleSelected
              const onToggleAll = () => {
                setPendingFilters(pf => {
                  const current = new Set(pf[activeCat] || [])
                  if (allVisibleSelected) {
                    visibleValues.forEach(v => current.delete(v))
                  } else {
                    visibleValues.forEach(v => current.add(v))
                  }
                  return { ...pf, [activeCat]: Array.from(current) }
                })
              }
              return visible.length === 0 ? null : (
                <div style={{ padding: '6px 14px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Checkbox
                    indeterminate={someVisibleSelected}
                    checked={allVisibleSelected}
                    onChange={onToggleAll}
                  >
                    <span style={{ fontSize: 12.5, fontWeight: 500 }}>
                      Select all ({visible.length})
                    </span>
                  </Checkbox>
                  {visibleSelectedCount > 0 && (
                    <Button
                      type="link"
                      size="small"
                      style={{ marginLeft: 'auto', padding: 0, fontSize: 12 }}
                      onClick={() => setPendingFilters(pf => ({ ...pf, [activeCat]: [] }))}
                    >
                      Clear ({visibleSelectedCount})
                    </Button>
                  )}
                </div>
              )
            })()}
            <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px' }}>
              {getCatOptions(activeCat).length === 0 ? (
                <div style={{ color: '#bbb', fontSize: 13, padding: '16px 0' }}>No options found</div>
              ) : (
                getCatOptions(activeCat).map(opt => {
                  const checked = (pendingFilters[activeCat] || []).includes(opt.value)
                  return (
                    <div
                      key={opt.value}
                      onClick={() => toggleCheck(activeCat, opt.value)}
                      style={{
                        padding: '7px 4px', borderBottom: '1px solid #f7f7f7',
                        display: 'flex', alignItems: 'center', gap: 10,
                        cursor: 'pointer', borderRadius: 4,
                        background: checked ? '#f0f2ff' : 'transparent',
                      }}
                    >
                      <Checkbox checked={checked} onChange={() => {}}>
                        <span style={{ fontSize: 13 }}>{opt.label}</span>
                      </Checkbox>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button size="small" onClick={() => setPendingFilters(EMPTY_PENDING)} style={{ color: '#aaa' }}>
            Clear all
          </Button>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={() => setRoleFilterOpen(false)}>Cancel</Button>
            <Button type="primary" onClick={handleApplyFilter} style={{ background: '#3249D7', borderColor: '#3249D7' }}>
              Apply Filter
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Bulk Email Modal ── */}
      <Modal
        open={emailModal.open}
        onCancel={() => setEmailModal({ open: false })}
        title={<span><MailOutlined style={{ marginRight: 8, color: '#059669' }} />Send Email to Selected Candidates</span>}
        footer={null}
        width={600}
      >
        {(() => {
          // Don't derive count from `filtered` — that's only the current
          // page. After "Select all (N) across pages" the modal would
          // misleadingly say "Sending to 3" while actually sending to N.
          // selectedRowKeys is gated by getCheckboxProps.disabled (no email
          // → no checkbox) plus fetchAllMatchingIds drops empty emails, so
          // every id here is guaranteed to have a primary_email.
          const totalSelected = selectedRowKeys.length
          return (
            <div style={{ padding: '8px 0' }}>
              <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f0fdf4', borderRadius: 8, fontSize: 13, color: '#065f46' }}>
                Sending to <strong>{totalSelected}</strong> candidate{totalSelected !== 1 ? 's' : ''} with email
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>Subject</div>
                <Input
                  value={emailSubject}
                  onChange={e => setEmailSubject(e.target.value)}
                  placeholder="Email subject…"
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>Message</div>
                <Input.TextArea
                  value={emailMessage}
                  onChange={e => setEmailMessage(e.target.value)}
                  rows={5}
                  placeholder="Write your message here… (student name and apply link will be included automatically)"
                />
                <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
                  The email will include the student's name, your message, and a direct Apply Now button.
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button onClick={() => setEmailModal({ open: false })}>Cancel</Button>
                <Button
                  type="primary"
                  icon={<MailOutlined />}
                  loading={emailSending}
                  disabled={totalSelected === 0 || !emailSubject.trim()}
                  onClick={handleSendEmails}
                  style={{ background: '#059669', borderColor: '#059669' }}
                >
                  Send to {totalSelected} Candidate{totalSelected !== 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          )
        })()}
      </Modal>

      {/* ── Score Weights Drawer ── */}
      <Drawer
        title={<span><SettingOutlined style={{ marginRight: 8, color: '#d97706' }} />Configure Score Weights</span>}
        open={weightsOpen}
        onClose={() => setWeightsOpen(false)}
        width={360}
        styles={{ body: { padding: 0 }, header: { padding: '16px 24px' } }}
      >
        <div style={{ padding: '20px 24px' }}>
          <div style={{ fontSize: 13, color: '#555', marginBottom: 20, lineHeight: 1.5 }}>
            Set how much each criterion contributes to the match score. Weights must sum to <strong>100</strong>.
          </div>

          {[
            { key: 'degree',   label: 'Degree Match',      color: '#3249D7', hint: 'Degree + stream alignment with role requirements' },
            { key: 'skill',    label: 'Skills',             color: '#7c3aed', hint: 'Matched skills vs required skills for the role' },
            { key: 'marks',    label: 'Academic Marks',     color: '#059669', hint: 'Diploma marks (20pts) + 10th marks (5pts) combined' },
            { key: 'arrear',   label: 'Arrears',            color: '#d97706', hint: '0 arrears = full pts; ≤2 = half; more = 0' },
            { key: 'location', label: 'Location',           color: '#0891b2', hint: 'Candidate state matches company location' },
          ].map(({ key, label, color, hint }) => (
            <div key={key} style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{label}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color }}>{pendingWeights[key] || 0}%</span>
              </div>
              <Slider
                min={0} max={100}
                value={pendingWeights[key] || 0}
                onChange={v => setPendingWeights(pw => ({ ...pw, [key]: v }))}
                trackStyle={{ background: color }}
                handleStyle={{ borderColor: color }}
                tooltip={{ formatter: v => `${v}%` }}
              />
              <div style={{ fontSize: 11, color: '#aaa' }}>{hint}</div>
            </div>
          ))}

          <div style={{ padding: '12px 14px', borderRadius: 8, marginBottom: 16, background: weightsTotal === 100 ? '#f0fdf4' : '#fff7ed', border: `1px solid ${weightsTotal === 100 ? '#86efac' : '#fed7aa'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, fontSize: 13, color: weightsTotal === 100 ? '#065f46' : '#9a3412' }}>
                {weightsTotal === 100
                  ? '✓ Weights sum to 100'
                  : <span><WarningOutlined style={{ marginRight: 6 }} />Total: {weightsTotal} (must be 100)</span>}
              </span>
              <Button size="small" onClick={() => setPendingWeights({ ...DEFAULT_WEIGHTS })} style={{ color: '#aaa', fontSize: 11 }}>
                Reset defaults
              </Button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={() => setWeightsOpen(false)} style={{ flex: 1 }}>Cancel</Button>
            <Button
              type="primary"
              disabled={weightsTotal !== 100}
              onClick={handleWeightsApply}
              style={{ flex: 1, background: '#d97706', borderColor: '#d97706' }}
            >
              Apply Weights
            </Button>
          </div>
        </div>
      </Drawer>

    </div>
  )
}

export default FindCandidatesTab
